'use client';

import {useState, Fragment, useEffect, useMemo, useCallback, useRef} from "react";

import { motion } from "framer-motion";
import {Tab, TabGroup, TabList, TabPanel, TabPanels} from "@headlessui/react";
import TourCard, {TourProps} from "@/components/ui/TourCard";
import TourCardSkeleton from "@/components/ui/TourCardSkeleton";
import {Swiper, SwiperSlide} from "swiper/react";
import {Autoplay} from "swiper/modules";
import type {Swiper as SwiperType} from "swiper";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import type { PageUrlMap } from "@/types/blog";
import { UrlBuilder } from "@/lib/url-builder";
import { shouldEmbedBlockPropsInDom } from "@/lib/embed-block-props-dom";

/** Laravel /api/products listesindeki tek ürünü TourCard modeline çevirir */
function mapApiProductToTourProps(
    product: Record<string, unknown>,
    urlBuilder: UrlBuilder | null,
): TourProps {
    const transformImageUrl = (url: string | undefined): string => {
        if (!url) {
            return 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920&h=1200&fit=crop';
        }
        if (url.startsWith('/api/media/proxy/')) {
            return url;
        }
        if (url.includes('/storage/')) {
            const pathMatch = url.match(/\/storage\/(.+)$/);
            if (pathMatch) {
                return `/api/media/proxy/${pathMatch[1]}`;
            }
        }
        return url;
    };

    const metas = (product.metas as Record<string, unknown>) || {};
    const gallery = (Array.isArray(product.gallery) ? product.gallery : []) as Array<Record<string, unknown>>;
    const metaImages = (Array.isArray(metas.images) ? metas.images : []) as string[];
    const name = (product.name as string) || (product.title as string) || 'Tour';
    const images =
        metaImages.length > 0
            ? metaImages.map((url: string) => transformImageUrl(url))
            : gallery.length > 0
              ? gallery.map((item) => transformImageUrl(String(item.url || item.src || '')))
              : [transformImageUrl((product.cover_image as string) || (product.image as string))];

    const imageAlt = (metas.image_alt as string[] | undefined) || images.map(() => name || 'Tour image');

    const categories = product.categories as Array<{ name?: string; slug?: string }> | undefined;

    return {
        src: images.length > 0 ? images : ['https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920&h=1200&fit=crop'],
        alt: imageAlt.length > 0 ? imageAlt : images.map(() => name || 'Tour image'),
        type: (product.type as string) || 'tour',
        imageCount:
            (metas.image_count as number) ||
            gallery.filter((item) => item.type === 'image').length ||
            images.length,
        videoCount:
            (metas.video_count as number) ||
            gallery.filter((item) => item.type === 'video').length ||
            0,
        title: name,
        transferType: (metas.transfer_type as string) || 'Private',
        start: (metas.start_location as string) || (metas.departure_city as string) || 'Istanbul',
        stops: (metas.stops as string | undefined) ?? (() => {
            const s = metas.station_city;
            if (s == null) return 'Multiple';
            if (Array.isArray(s)) return s.join(', ');
            if (typeof s === 'string') {
                try {
                    const a = JSON.parse(s);
                    return Array.isArray(a) ? a.join(', ') : s;
                } catch {
                    return s;
                }
            }
            return String(s);
        })(),
        end: (metas.end_location as string) || (metas.end_city as string) || 'Istanbul',
        tourDuration: (metas.tour_duration as string) || '1 Day',
        tag: product.is_featured ? 'Featured' : (categories?.[0]?.name || 'Tour'),
        price: {
            discount_price: (product.discount_price as string) || (product.price as string) || '0',
            price: (product.price as string) || '0',
        },
        calculated_price: product.calculated_price as TourProps['calculated_price'],
        reviews: metas.reviews_count ? parseInt(String(metas.reviews_count), 10) : 0,
        tourId: String(product.id),
        productId: (() => {
            const code = metas.product_id != null ? String(metas.product_id).trim() : ''
            return code || String(product.id)
        })(),
        brochureProductId: typeof product.id === 'number' ? product.id : Number(product.id) || undefined,
        slug: product.slug as string | undefined,
        url: urlBuilder
            ? urlBuilder.buildProductDetailUrl(
                  (product.type as string) || 'tour',
                  String(product.slug ?? ''),
                  categories?.[0]?.slug,
              )
            : undefined,
        is_featured: Boolean(product.is_featured),
        transfer: (metas.transfer as TourProps['transfer']) ||
            (Array.isArray(metas.field_transfer) ? metas.field_transfer : []),
        tourDurationDay: (metas.tour_duration_day as number | undefined) || (metas.duration_day as number | undefined),
        tourDurationNight: (metas.tour_duration_night as number | undefined) || (metas.duration_night as number | undefined),
        tourRoute: (() => {
            const dep = metas.departure_city != null ? String(metas.departure_city).trim() : '';
            const end = metas.end_city != null ? String(metas.end_city).trim() : '';
            let stations: string[] = [];
            if (metas.station_city != null) {
                const raw = metas.station_city;
                if (Array.isArray(raw)) stations = raw.map((s: unknown) => String(s).trim()).filter(Boolean);
                else if (typeof raw === 'string') {
                    try {
                        const p = JSON.parse(raw);
                        stations = Array.isArray(p)
                            ? p.map((s: unknown) =>
                                  s && typeof (s as { value?: string }).value === 'string'
                                      ? (s as { value: string }).value
                                      : String(s),
                              )
                              .map((s) => s.trim())
                              .filter(Boolean)
                            : [];
                    } catch {
                        stations = [raw.trim()].filter(Boolean);
                    }
                }
            }
            if (dep || stations.length > 0 || end) {
                const parts: Array<{ location_title?: string }> = [];
                if (dep) parts.push({ location_title: dep });
                stations.forEach((s) => parts.push({ location_title: s }));
                if (end) parts.push({ location_title: end });
                if (parts.length > 0) return parts;
            }
            return Array.isArray(metas.tour_route) ? (metas.tour_route as TourProps['tourRoute']) : [];
        })(),
        pricingPeriods: (metas.pricing_periods as TourProps['pricingPeriods']) || (metas.tour_pricing as TourProps['pricingPeriods']) || [],
        tourType: (metas.tour_type as string) || (metas.group_type as string) || '',
    };
}

interface CategoryData {
    id: number;
    name: string;
    slug?: string;
    description?: string;
    cover_image?: string;
    type?: string;
}

interface Cats {
    id: string;
    title: string;
    tours: TourProps[];
}

interface TourCatTabsProps {
    categoryIds?: number[];
    languageId?: number;
    language?: string;
    languageCode?: string;
    categories?: CategoryData[];
    enableClientFetch?: boolean;
    pageUrls?: PageUrlMap;
    defaultLanguage?: string;
    /** Trait / blok başlığı */
    title?: string;
    subTitle?: string;
    __pageContext?: {
        languageId?: number;
        language?: string;
        languageCode?: string;
    };
}

/** Kategori listesi: `ProductCategoriesContext` doluysa ek fetch yok; aksi halde `/api/product-categories`. @see `frontend/docs/tabs-and-header-data.md` */
export default function TourCatTabs({
    categoryIds: propCategoryIds,
    languageId: propLanguageId,
    language: propLanguage,
    languageCode: propLanguageCode,
    categories: initialCategories,
    enableClientFetch = false,
    pageUrls,
    defaultLanguage = 'en',
    title: titleProp,
    subTitle: subTitleProp,
    __pageContext
}: TourCatTabsProps = {}) {

    const languageId = propLanguageId || __pageContext?.languageId;
    const resolvedLanguageCode = propLanguage || propLanguageCode || __pageContext?.language || __pageContext?.languageCode;
    const urlBuilder = useMemo(
        () =>
            pageUrls
                ? new UrlBuilder(pageUrls, resolvedLanguageCode || 'en', defaultLanguage)
                : null,
        [pageUrls, resolvedLanguageCode, defaultLanguage],
    );
    const [categories, setCategories] = useState<CategoryData[]>(initialCategories || []);
    const [loading, setLoading] = useState(!initialCategories);
    const [error, setError] = useState<string | null>(null);
    const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [hoveredTabIndex, setHoveredTabIndex] = useState<number | null>(null);
    const [componentProps, setComponentProps] = useState<{
        categoryIds?: number[];
        title?: string;
        subTitle?: string;
    }>({});
    const [variables, setVariables] = useState<Record<string, string>>({});
    const [toursByCategory, setToursByCategory] = useState<Record<number, TourProps[]>>({});
    const [toursLoading, setToursLoading] = useState<Record<number, boolean>>({});
    /** Aktif sekme + hover ön-yükleme için tekrarlı istek önleme */
    const loadedCategoryIdsRef = useRef<Set<number>>(new Set());
    const inflightCategoryIdsRef = useRef<Set<number>>(new Set());

    const hasInitialData = initialCategories && initialCategories.length > 0;

    // Helper function to get variable with fallback
    const getVariable = (key: string, fallback: string): string => {
        return variables[key] || fallback;
    };

    // Language variables - Context'ten al (duplicate fetch'i önlemek için)
    const { variables: contextVariables } = useLanguageVariables();
    
    // Context'ten gelen variables'ı kullan, yoksa boş object
    useEffect(() => {
        if (contextVariables && Object.keys(contextVariables).length > 0) {
            setVariables(contextVariables);
        }
    }, [contextVariables]);

    // Component hydration: data-component-props'tan props'ları al
    useEffect(() => {
        if (!shouldEmbedBlockPropsInDom()) return;
        const updateProps = () => {
            const element = document.querySelector('[data-component-path="sections/TourCatTabs"]');
            if (element) {
                const propsAttr = element.getAttribute('data-component-props');
                if (propsAttr) {
                    try {
                        const props = JSON.parse(propsAttr);
                        setComponentProps(props);
                    } catch {
                        // Props parse hatası
                    }
                }
            }
        };

        updateProps();

        const observer = new MutationObserver(updateProps);
        const element = document.querySelector('[data-component-path="sections/TourCatTabs"]');
        if (element) {
            observer.observe(element, {
                attributes: true,
                attributeFilter: ['data-component-props']
            });
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    // categoryIds'i belirle (öncelik sırası: prop > componentProps)
    const categoryIds = propCategoryIds || componentProps.categoryIds || undefined;

    const sectionTitle =
        (titleProp && String(titleProp).trim()) ||
        (componentProps.title && String(componentProps.title).trim()) ||
        'Domestic Tours';
    const sectionSubTitle =
        (subTitleProp && String(subTitleProp).trim()) ||
        (componentProps.subTitle && String(componentProps.subTitle).trim()) ||
        undefined;

    useEffect(() => {
        if (hasInitialData) {
            setLoading(false);
            return;
        }

        async function fetchCategories() {
            if (!categoryIds || categoryIds.length === 0) {
                setLoading(false);
                setCategories([]);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const params = new URLSearchParams();
                
                if (languageId) {
                    params.set('language_id', String(languageId));
                }

                if (categoryIds && categoryIds.length > 0) {
                    categoryIds.forEach(id => params.append('category_ids[]', String(id)));
                }

                if (resolvedLanguageCode) {
                    params.set('language', resolvedLanguageCode);
                }

                const response = await fetch(`/api/product-categories?${params.toString()}`);
                
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const errorMessage = errorData.message || errorData.error || `API error: ${response.status}`;
                    throw new Error(errorMessage);
                }

                const data = await response.json();
                setCategories(data.data || []);
            } catch (err) {
                console.error('Kategoriler yüklenirken hata:', err);
                const errorMessage = err instanceof Error ? err.message : 'Kategoriler yüklenemedi';
                setError(errorMessage);
                setCategories([]);
            } finally {
                setLoading(false);
            }
        }

        fetchCategories();
    }, [languageId, categoryIds, hasInitialData, enableClientFetch, resolvedLanguageCode]);

    const categoryListIdentity = useMemo(() => categories.map((c) => c.id).join(','), [categories]);

    useEffect(() => {
        loadedCategoryIdsRef.current.clear();
        inflightCategoryIdsRef.current.clear();
        setToursByCategory({});
        setToursLoading({});
    }, [categoryListIdentity]);

    const fetchToursForCategory = useCallback(
        async (categoryId: number, signal?: AbortSignal) => {
            if (loadedCategoryIdsRef.current.has(categoryId)) return;
            if (inflightCategoryIdsRef.current.has(categoryId)) return;
            inflightCategoryIdsRef.current.add(categoryId);
            setToursLoading((prev) => ({ ...prev, [categoryId]: true }));
            try {
                const params = new URLSearchParams();
                if (languageId) {
                    params.set('language_id', String(languageId));
                }
                if (resolvedLanguageCode) {
                    params.set('language', resolvedLanguageCode);
                }
                params.set('category_id', String(categoryId));
                params.set('is_active', '1');
                params.set('limit', '10');
                params.set('calculate_prices', 'true');

                const response = await fetch(`/api/products?${params.toString()}`, { signal });

                if (!response.ok) {
                    setToursByCategory((prev) => ({ ...prev, [categoryId]: [] }));
                    loadedCategoryIdsRef.current.add(categoryId);
                    return;
                }

                const data = await response.json();
                const products = (data.data || []) as Record<string, unknown>[];
                const transformedTours = products.map((product) => mapApiProductToTourProps(product, urlBuilder));
                setToursByCategory((prev) => ({ ...prev, [categoryId]: transformedTours }));
                loadedCategoryIdsRef.current.add(categoryId);
            } catch (err) {
                if ((err as Error).name === 'AbortError') return;
                console.error(`Kategori ${categoryId} için turlar yüklenirken hata:`, err);
                setToursByCategory((prev) => ({ ...prev, [categoryId]: [] }));
                loadedCategoryIdsRef.current.add(categoryId);
            } finally {
                inflightCategoryIdsRef.current.delete(categoryId);
                setToursLoading((prev) => ({ ...prev, [categoryId]: false }));
            }
        },
        [languageId, resolvedLanguageCode, urlBuilder],
    );

    // İlk yüklemede yalnızca aktif sekmenin kategorisi; sekme değişiminde veya hover'da diğerleri
    useEffect(() => {
        if (categories.length === 0) return;
        const cat = categories[activeIndex];
        if (!cat) return;
        const ac = new AbortController();
        void fetchToursForCategory(cat.id, ac.signal);
        return () => ac.abort();
    }, [categories, activeIndex, fetchToursForCategory]);

    useEffect(() => {
        if (categories.length === 0 || hoveredTabIndex == null) return;
        if (hoveredTabIndex === activeIndex) return;
        const cat = categories[hoveredTabIndex];
        if (!cat) return;
        void fetchToursForCategory(cat.id);
    }, [hoveredTabIndex, activeIndex, categories, fetchToursForCategory]);

    // Kategorileri Cats formatına dönüştür (turları dahil et)
    const catsWithTours: Cats[] = categories.map((category) => ({
        id: String(category.id),
        title: category.name,
        tours: toursByCategory[category.id] || []
    }));

    // Eğer kategori yoksa boş state göster
    if (!loading && categories.length === 0) {
        return (
            <section className="w-full lg:w-8/12 xl:w-9/12 mx-auto px-4 sm:px-6 lg:px-0 py-16 sm:py-24 md:py-32 lg:py-40">
                <div className="text-center text-alsero-light-gray border-2 border-dashed border-gray-200 rounded-2xl p-6 sm:p-8 text-sm sm:text-base">
                    {!categoryIds || categoryIds.length === 0 
                        ? getVariable('categories_no_selection', 'Bu component için kategori seçimi yapılmamış. Lütfen sayfa editöründe component\'i seçin ve "Kategorileri Seç" butonunu kullanın.')
                        : getVariable('categories_not_found', 'Seçilen kategoriler bulunamadı.')
                    }
                </div>
            </section>
        );
    }

    if (loading) {
        return (
            <section className="w-full lg:w-8/12 xl:w-9/12 mx-auto px-4 sm:px-6 lg:px-0 py-16 sm:py-24 md:py-32">
                <div className="text-center text-gray-500 text-sm sm:text-base">
                    {getVariable('categories_loading', 'Kategoriler yükleniyor...')}
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="w-full lg:w-8/12 xl:w-9/12 mx-auto px-4 sm:px-6 lg:px-0 py-16 sm:py-24 md:py-32">
                <div className="text-center text-red-500 text-sm sm:text-base">
                    {getVariable('categories_load_error', 'Hata:')} {error}
                </div>
            </section>
        );
    }

    // Eğer kategoriler varsa onları kullan, yoksa boş liste
    const finalCatsWithTours = catsWithTours.length > 0 ? catsWithTours : [];

    if (finalCatsWithTours.length === 0) {
        return (
            <section className="w-full lg:w-8/12 xl:w-9/12 mx-auto px-4 sm:px-6 lg:px-0 py-16 sm:py-24 md:py-32 lg:py-40">
                <div className="text-center text-alsero-light-gray border-2 border-dashed border-gray-200 rounded-2xl p-6 sm:p-8 text-sm sm:text-base">
                    {getVariable('categories_no_selection', 'Bu component için kategori seçimi yapılmamış. Lütfen sayfa editöründe component\'i seçin ve "Kategorileri Seç" butonunu kullanın.')}
                </div>
            </section>
        );
    }

    return (
        <section className="w-full lg:w-8/12 xl:w-9/12 mx-auto px-4 sm:px-6 lg:px-0 min-w-0">
            <div className="flex flex-col gap-4 sm:gap-6 min-w-0">
                <div className="flex flex-col gap-1 sm:gap-2 text-center sm:text-left min-w-0">
                    <h2
                        className="font-semibold text-xl sm:text-2xl md:text-3xl lg:text-4xl text-alsero-second leading-tight"
                        data-prop-path="title"
                    >
                        {sectionTitle}
                    </h2>
                    {sectionSubTitle ? (
                        <p
                            className="text-sm sm:text-base md:text-lg text-alsero-light-gray max-w-prose mx-auto sm:mx-0"
                            data-prop-path="subTitle"
                        >
                            {sectionSubTitle}
                        </p>
                    ) : null}
                </div>
                <TabGroup selectedIndex={activeIndex} onChange={setActiveIndex}>
                    <TabList className="relative flex max-w-full min-w-0 flex-row flex-nowrap justify-start gap-x-2 gap-y-1 overflow-x-auto overscroll-x-contain pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden touch-pan-x after:absolute after:inset-x-0 after:bottom-0 after:h-[4px] after:bg-[#E1E1E1] after:rounded-full sm:flex-wrap sm:justify-center sm:overflow-visible sm:gap-x-4 sm:gap-y-0">
                        {(() => {
                            const indicatorIdx = hoveredTabIndex ?? activeIndex;
                            return finalCatsWithTours.map((cat, index) => {
                                return (
                                    <Tab key={cat.id} as={Fragment}>
                                        {() => (

                                            <button
                                                type="button"
                                                onClick={() => setActiveIndex(index)}
                                                onMouseEnter={() => setHoveredTabIndex(index)}
                                                onMouseLeave={() => setHoveredTabIndex(null)}
                                                className="relative z-10 shrink-0 cursor-pointer whitespace-nowrap pb-3 sm:pb-5 px-2 sm:px-3 text-sm sm:text-base md:text-lg transition-colors duration-300 ease-in-out text-alsero-second hover:text-alsero-first focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-alsero-first/30 text-center leading-tight"
                                            >
                                                <span className="relative">{cat.title}</span>

                                                {indicatorIdx === index && (
                                                    <motion.div
                                                        layoutId="moving-underline"
                                                        className="absolute left-0 right-0 bottom-0 h-[3px] rounded-full bg-alsero-first"
                                                        transition={{
                                                            type: "spring",
                                                            stiffness: 500,
                                                            damping: 40,
                                                            mass: 0.6,
                                                        }}
                                                    />
                                                )}
                                            </button>
                                        )}
                                    </Tab>
                                )
                            });
                        })()}
                    </TabList>
                    <TabPanels>
                        {finalCatsWithTours.map((cat) => {
                            const categoryId = Number(cat.id);
                            const isLoadingTours = toursLoading[categoryId] === true;
                            const hasTours = cat.tours.length > 0;
                            
                            return (
                            <TabPanel key={cat.id}>
                                {isLoadingTours ? (
                                    <Swiper
                                        modules={[Autoplay]}
                                        loop={false}
                                        autoplay={false}
                                        slidesPerView={1}
                                        spaceBetween={16}
                                        breakpoints={{
                                            640: {
                                                slidesPerView: 1,
                                                spaceBetween: 20,
                                            },
                                            768: {
                                                slidesPerView: 2,
                                                spaceBetween: 20,
                                            },
                                            1024: {
                                                slidesPerView: 3,
                                                spaceBetween: 20,
                                            },
                                            1280: {
                                                slidesPerView: 3,
                                                spaceBetween: 24,
                                            },
                                            1536: {
                                                slidesPerView: 4,
                                                spaceBetween: 24,
                                            },
                                        }}
                                    >
                                        {Array.from({ length: 4 }).map((_, index) => (
                                            <SwiperSlide key={index} className="py-6 sm:py-10">
                                                <TourCardSkeleton index={index} viewType="box" />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                ) : hasTours ? (
                                    <>
                                        <Swiper
                                            modules={[Autoplay]}
                                            loop={cat.tours.length > 4}
                                            watchOverflow
                                            observer
                                            observeParents
                                            autoplay={false}
                                            slidesPerView={1}
                                            spaceBetween={16}
                                            onSwiper={(swiper) => setSwiperInstance(swiper)}
                                            breakpoints={{
                                                640: {
                                                    slidesPerView: 1,
                                                    spaceBetween: 20,
                                                },
                                                768: {
                                                    slidesPerView: 2,
                                                    spaceBetween: 20,
                                                },
                                                1024: {
                                                    slidesPerView: 3,
                                                    spaceBetween: 20,
                                                },
                                                1280: {
                                                    slidesPerView: 3,
                                                    spaceBetween: 24,
                                                },
                                                1536: {
                                                    slidesPerView: 4,
                                                    spaceBetween: 24,
                                                },
                                            }}
                                            className="!overflow-hidden"
                                        >
                                            {cat.tours.map((tour,index) => (
                                                <SwiperSlide key={tour.tourId || tour.slug || index} className="!h-auto py-6 sm:py-10">
                                                    <TourCard 
                                                        tour={tour} 
                                                        viewType="box" 
                                                        index={index}
                                                        languageId={languageId}
                                                        language={resolvedLanguageCode}
                                                        languageCode={resolvedLanguageCode}
                                                        pageUrls={pageUrls}
                                                        defaultLanguage={defaultLanguage}
                                                    />
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                        <motion.div
                                            initial={{ opacity: 1, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 1, duration: 0.6 }}
                                            className="relative mt-4 flex items-center justify-center gap-3 sm:mt-8 sm:gap-5 lg:mt-14"
                                        >
                                            <motion.button
                                                type="button"
                                                onClick={() => swiperInstance?.slidePrev()}
                                                className="p-3 sm:p-5 border border-alsero-dark-gray rounded-xl sm:rounded-2xl transition-all ease-in-out cursor-pointer duration-300 touch-manipulation"
                                                aria-label="Previous slide"
                                                whileHover={{
                                                    x: -5,
                                                    transition: { duration: 0.2 }
                                                }}
                                            >
                                                <motion.svg
                                                    width="28"
                                                    height="25"
                                                    viewBox="0 0 28 25"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="fill-alsero-dark-gray"
                                                    whileHover={{ x: -3 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    <path d="M0.447544 13.6676L10.5593 24.5202C10.848 24.83 11.2327 25 11.6429 25C12.0536 25 12.438 24.8297 12.7267 24.5202L13.6448 23.5346C13.9332 23.2253 14.0921 22.8122 14.0921 22.3717C14.0921 21.9314 13.9332 21.5044 13.6448 21.1951L7.7457 14.85L26.4873 14.85C27.3323 14.85 28 14.1401 28 13.2329V11.8396C28 10.9325 27.3323 10.1509 26.4873 10.1509L7.67877 10.1509L13.6445 3.77047C13.933 3.46068 14.0919 3.05879 14.0919 2.61829C14.0919 2.17829 13.933 1.77053 13.6445 1.46099L12.7265 0.478606C12.4378 0.168819 12.0533 -8.34465e-07 11.6427 -8.34465e-07C11.2325 -8.34465e-07 10.8478 0.171018 10.5591 0.480805L0.447317 11.3332C0.157987 11.6439 -0.00113344 12.0588 3.33786e-06 12.4998C-0.000906467 12.9422 0.157985 13.3573 0.447544 13.6676Z"/>
                                                </motion.svg>
                                            </motion.button>
                                            <motion.button
                                                type="button"
                                                onClick={() => swiperInstance?.slideNext()}
                                                className="p-3 sm:p-5 border border-alsero-dark-gray rounded-xl sm:rounded-2xl transition-all ease-in-out cursor-pointer duration-300 touch-manipulation"
                                                aria-label="Next slide"
                                                whileHover={{
                                                    x: 5,
                                                    transition: { duration: 0.2 }
                                                }}
                                            >
                                                <motion.svg
                                                    width="28"
                                                    height="25"
                                                    viewBox="0 0 28 25"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="fill-alsero-dark-gray"
                                                    whileHover={{ x: 3 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    <path d="M27.5525 11.3324L17.4407 0.479833C17.152 0.170044 16.7673 2.93169e-06 16.3571 2.85997e-06C15.9464 2.78816e-06 15.562 0.170288 15.2733 0.479832L14.3552 1.46539C14.0668 1.77469 13.9079 2.18782 13.9079 2.62832C13.9079 3.06857 14.0668 3.49562 14.3552 3.80492L20.2543 10.15L1.51267 10.15C0.667668 10.15 -1.89881e-06 10.8599 -2.05742e-06 11.7671L-2.30103e-06 13.1604C-2.45964e-06 14.0675 0.667667 14.8491 1.51267 14.8491L20.3212 14.8491L14.3555 21.2295C14.067 21.5393 13.9081 21.9412 13.9081 22.3817C13.9081 22.8217 14.067 23.2295 14.3555 23.539L15.2735 24.5214C15.5622 24.8312 15.9467 25 16.3573 25C16.7675 25 17.1522 24.829 17.4409 24.5192L27.5527 13.6668C27.842 13.3561 28.0011 12.9412 28 12.5003C28.0009 12.0578 27.842 11.6427 27.5525 11.3324Z"/>
                                                </motion.svg>
                                            </motion.button>
                                        </motion.div>
                                    </>
                                ) : (
                                    <div className="py-12 sm:py-20 text-center text-alsero-light-gray text-sm sm:text-base px-2">
                                        {getVariable('tours_no_tours_for_category', 'Bu kategori için henüz tur bulunmuyor.')}
                                    </div>
                                )}
                            </TabPanel>
                            );
                        })}
                    </TabPanels>
                </TabGroup>
            </div>
        </section>
    )
}

// Eski statik veriler kaldırıldı - artık API'den dinamik olarak çekiliyor
